import * as React from "react";
import { useErrorLog } from "../error-provider/use-log.mjs";
const usePropsChecker = (props) => {
  const { componentName, componentProps, defaultValueName, valueName, onChangeNames, onChangeAltNames, isComponentDisabled } = props;
  const { log } = useErrorLog();
  const isInitial = React.useRef(true);
  const didWarnControlledToUncontrolled = React.useRef(false);
  const didWarnUncontrolledToControlled = React.useRef(false);
  const checkHasOnChange = () => {
    const onChangeNamesArray = Array.isArray(onChangeNames) ? onChangeNames : [onChangeNames];
    const onChangeAltNamesArray = Array.isArray(onChangeAltNames) ? onChangeAltNames : onChangeAltNames ? [onChangeAltNames] : [];
    return onChangeNamesArray.every((onChangeName) => !!componentProps[onChangeName]) || onChangeAltNamesArray && onChangeAltNamesArray.length > 0 && onChangeAltNamesArray.every((onChangeName) => !!componentProps[onChangeName]);
  };
  const hasOnChange = checkHasOnChange();
  const checkIsControlled = () => {
    const isDefined = typeof componentProps[valueName] !== "undefined";
    if (isComponentDisabled) {
      return isDefined;
    }
    return hasOnChange && isDefined;
  };
  const newIsControlled = checkIsControlled();
  const isControlledRef = React.useRef(newIsControlled);
  React.useEffect(() => {
    if (isInitial.current) {
      const hasValuePropWithoutOnChangeProp = typeof componentProps[valueName] !== "undefined" && !hasOnChange;
      if (hasValuePropWithoutOnChangeProp && !isComponentDisabled) {
        const formattedNames = (names) => Array.isArray(names) ? names.join(", ") : names;
        log(
          'You provided a "'.concat(valueName, '" prop without an "').concat(formattedNames(onChangeNames), '"').concat(onChangeAltNames ? ' or "' + formattedNames(onChangeAltNames) + '"' : "", ' handler in "').concat(componentName, '" component. If the state is handled outside component, set "').concat(formattedNames(onChangeNames), '"').concat(onChangeAltNames ? ' or "' + formattedNames(onChangeAltNames) + '"' : "", ' prop. Otherwise use "').concat(defaultValueName, '" prop.')
        );
      }
      isInitial.current = false;
    }
  }, [componentName, componentProps, defaultValueName, hasOnChange, isComponentDisabled, onChangeAltNames, onChangeNames, valueName, log]);
  React.useEffect(() => {
    if (!isControlledRef.current && newIsControlled && !didWarnUncontrolledToControlled.current) {
      log(
        'The "'.concat(componentName, '" component is changing from uncontrolled to be controlled. This is likely caused by the "').concat(valueName, '" changing from undefined to a defined value, which should not happen.')
      );
      didWarnUncontrolledToControlled.current = true;
    }
    if (isControlledRef.current && !newIsControlled && !didWarnControlledToUncontrolled.current) {
      log(
        'The "'.concat(componentName, '" component is changing from controlled to be uncontrolled. This is likely caused by the "').concat(valueName, '" changing from a defined to undefined value, which should not happen.')
      );
      didWarnControlledToUncontrolled.current = true;
    }
    isControlledRef.current = newIsControlled;
  }, [componentProps, componentName, newIsControlled, valueName, log]);
  return {
    isControlled: newIsControlled
  };
};
const PropsChecker = (props) => {
  const { children, ...checkerOptions } = props;
  usePropsChecker(checkerOptions);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
};
export {
  PropsChecker,
  usePropsChecker
};
